import { useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useViewNavigate } from '../../hooks/useViewNavigate.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { Sparkle } from '../doodles/Doodles.js';
import { IconFire } from '../../screens/home/icons.js';
import { useProgressTotals } from '../../hooks/useProgressTotals.js';
import {
  IconHomeD,
  IconChatD,
  IconBookD,
  IconStatsD,
  IconUserD,
  IconChevronDownD,
} from './icons.js';
import { BrandMark } from '../BrandMark.js';

/**
 * Sidebar de navegación del layout de escritorio.
 *
 * La conversación tiene su PROPIO rail izquierdo (coach + errores) dentro de
 * ConversationDesktop, así que este sidebar no se usa ahí.
 *
 * Todos los items tienen ruta propia. Rutas sin item (ej. /program, /help)
 * no iluminan ninguno: antes caían al default 'home' y el sidebar mentía
 * ubicación.
 */

type NavKey =
  | 'home'
  | 'activity'
  | 'conversation'
  | 'scenarios'
  | 'listen'
  | 'library'
  | 'stats'
  | 'profile';

interface NavItem {
  key: NavKey;
  label: string;
  path: string;
  icon: ReactNode;
}

const NAV_ITEMS: readonly NavItem[] = [
  { key: 'home', label: 'Inicio', path: '/home', icon: <IconHomeD /> },
  {
    key: 'activity',
    label: 'Actividad',
    path: '/activity',
    icon: (
      <svg
        width={20}
        height={20}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
        <path d="M3 3v5h5" />
        <path d="M12 7v5l4 2" />
      </svg>
    ),
  },
  { key: 'conversation', label: 'Conversación', path: '/conversation', icon: <IconChatD /> },
  {
    key: 'scenarios',
    label: 'Escenarios',
    path: '/scenarios',
    icon: (
      <svg
        width={20}
        height={20}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="8.5" />
        <circle cx="12" cy="12" r="4.5" />
        <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    key: 'listen',
    label: 'Escuchar',
    path: '/listen',
    icon: (
      <svg
        width={20}
        height={20}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 14v-3a9 9 0 0 1 18 0v3" />
        <rect x="3" y="13" width="4" height="7" rx="1.5" />
        <rect x="17" y="13" width="4" height="7" rx="1.5" />
      </svg>
    ),
  },
  { key: 'library', label: 'Biblioteca', path: '/library', icon: <IconBookD /> },
  { key: 'stats', label: 'Progreso', path: '/progress', icon: <IconStatsD /> },
  { key: 'profile', label: 'Perfil', path: '/profile', icon: <IconUserD /> },
];

function activeKeyFor(pathname: string): NavKey | null {
  if (pathname.startsWith('/home')) return 'home';
  if (pathname.startsWith('/activity')) return 'activity';
  if (pathname.startsWith('/conversation')) return 'conversation';
  if (pathname.startsWith('/scenarios')) return 'scenarios';
  if (pathname.startsWith('/listen')) return 'listen';
  if (pathname.startsWith('/library')) return 'library';
  if (pathname.startsWith('/progress')) return 'stats';
  if (pathname.startsWith('/profile')) return 'profile';
  // Secciones sin item propio (/program, /help): ningún item activo.
  return null;
}

export function DesktopSidebar() {
  const location = useLocation();
  const navigate = useViewNavigate();
  const { user } = useAuth();
  const active = activeKeyFor(location.pathname);

  const { totals } = useProgressTotals();
  const name = user?.name ?? 'Tu cuenta';
  const email = user?.email ?? '';
  const initial = (name.trim()[0] ?? 'O').toUpperCase();

  return (
    <aside className="dt-side">
      {/* Marca */}
      <div className="dt-logo">
        <div className="dt-logo-mark">
          <BrandMark size={24} />
        </div>
        <div>
          <div className="script" style={{ fontSize: 22, lineHeight: 1, color: 'var(--text)' }}>
            Orborix
          </div>
          <div className="dt-logo-sub">Speak</div>
        </div>
      </div>

      {/* Navegación */}
      <nav className="dt-nav" aria-label="Navegación principal">
        {NAV_ITEMS.map((it) => (
          <button
            key={it.key}
            type="button"
            className={`dt-nav-item${it.key === active ? ' active' : ''}`}
            data-nav={it.key}
            aria-current={it.key === active ? 'page' : undefined}
            onClick={() => navigate(it.path)}
          >
            <span className="dt-nav-icon">{it.icon}</span>
            <span style={{ flex: 1, textAlign: 'left' }}>{it.label}</span>
          </button>
        ))}
      </nav>

      {/* Donde entra «Tu nave está en viaje» (ShipTravelNotice), debajo de Perfil: empuja en vez
          de tapar (card 599). `display: contents`: vacío no ocupa lugar ni suma un gap. */}
      <div className="dt-side-slot" data-side-slot="" />

      <div style={{ flex: 1 }} />

      {/* Racha (backend /progress/totals). Sin dato (cargando o error) la card
          no se muestra — nada de inventar un 0 ni una meta diaria ficticia. */}
      {totals && (
        <div className="dt-side-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div className="streak-chip">
              <IconFire />
              <span>{totals.currentStreak}</span>
              <span className="streak-chip__unit">{totals.currentStreak === 1 ? 'día' : 'días'}</span>
            </div>
            <Sparkle color="var(--lime)" size={14} />
          </div>
          <div className="dt-side-card__hint">
            Tu mejor marca:
            <span className="dt-side-card__hint-accent">{totals.bestStreak} {totals.bestStreak === 1 ? 'día' : 'días'}</span>
          </div>
        </div>
      )}

      {/* Perfil */}
      <button type="button" className="dt-profile" onClick={() => navigate('/profile')}>
        <div className="dt-avatar">{initial}</div>
        <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
          <div className="dt-profile__name">{name}</div>
          {email && <div className="dt-profile__email">{email}</div>}
        </div>
        <span style={{ color: 'var(--text-faint)', display: 'flex' }}>
          <IconChevronDownD size={14} />
        </span>
      </button>
    </aside>
  );
}
